import { useEffect } from "react";
import { isRouteErrorResponse, Link, useRouteError } from "react-router";
import { currentLang, tr } from "../lib/sprache";
import { istLadefehler, meldeFehler, neuLadenNachUpdate } from "../lib/fehlerbericht";
import { PublicLayout } from "../components/shell/WebLayout";
import { NichtGefunden } from "../pages/NichtGefunden";
import { MeldenKnopf } from "../components/Melden";

/**
 * Fehlerseite für Abstürze beim Anzeigen einer Seite (errorElement des Routers, 0158). Vorher stand dort die Entwicklerseite
 * von React Router („Unexpected Application Error!“) mit Stack. Jetzt: ruhige Karte im Rahmen der öffentlichen Zustandsseiten,
 * Neu laden als Hauptweg, der Fehler geht automatisch an den Betrieb. Fehlt nach einem Update ein Programmteil, lädt die
 * Seite einmal selbst neu.
 */
export function AppFehler() {
  const err = useRouteError();
  const notFound = isRouteErrorResponse(err) && err.status === 404;
  const message = err instanceof Error ? err.message : isRouteErrorResponse(err) ? `${err.status} ${err.statusText}` : String(err ?? "");
  const lade = istLadefehler(message);
  useEffect(() => {
    if (notFound) return;
    if (lade && neuLadenNachUpdate()) return;
    meldeFehler(lade ? "chunk" : "render", err);
  }, [err, notFound, lade]);
  if (notFound) return <NichtGefunden />;
  const lang = currentLang();
  const t = (de: string, en: string) => tr(lang, de, en);
  return (
    <PublicLayout lang={lang}>
      <title>{t("Fehler · CreatorOffice", "Error · CreatorOffice")}</title>
      <meta name="robots" content="noindex, nofollow" />
      <main id="main" className="auth-shell stack">
        <section className="centerform panel stack" role="alert">
          <h1>{lade ? t("Neue Version verfügbar", "New version available") : t("Da ist etwas schiefgelaufen", "Something went wrong")}</h1>
          <p>{lade
            ? t("CreatorOffice wurde gerade aktualisiert. Lade die Seite neu, um weiterzuarbeiten.", "CreatorOffice was just updated. Reload the page to continue.")
            : t("Diese Seite konnte nicht angezeigt werden. Der Fehler wurde automatisch an uns gemeldet. Meist hilft es, die Seite neu zu laden; deine gespeicherten Daten sind sicher.",
              "This page could not be displayed. The error was reported to us automatically. Reloading the page usually helps; your saved data is safe.")}</p>
          <div className="row">
            <button type="button" className="btn primary" onClick={() => location.reload()}>{t("Seite neu laden", "Reload page")}</button>
            <Link className="btn" to="/" reloadDocument>{t("Zur Startseite", "To the start page")}</Link>
            {!lade && <MeldenKnopf message={message || t("Seite konnte nicht angezeigt werden", "Page could not be displayed")} className="" />}
          </div>
          {message && !lade && (
            <details className="tk-fehler-details">
              <summary>{t("Technische Details", "Technical details")}</summary>
              <pre className="mono">{message.slice(0, 500)}</pre>
            </details>
          )}
        </section>
      </main>
    </PublicLayout>
  );
}
